import { useCallback, useEffect } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import { resetFormState, useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useCommissionPeriods } from './use-commission-periods';
import { useRevisionHistory } from './use-revision-history';

import { useDeleteMutation, useGetTeamMember, useUpdateMutation } from '../apis';
import { teamMemberFormToUpdate, teamMemberQueryToForm } from '../converters';
import { useFormDefinition } from '../form-context';
import type { TeamMemberFormSchema, TeamMemberQuerySchema, TeamMemberUpdateSchema } from '../schemas';

export function usePage() {
  const nav = useNavigate();
  const edit = useEdit();
  const form = useFormDefinition();
  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'team-members', id: `${id}` });
  const { setHasToInvalidate, removeQuery } = useInvalidate(['TEAM_MEMBER', id ?? '']);

  const teamMember = useGetTeamMember(`${id}`);
  const updateMutation = useUpdateMutation(`${id}`);
  const deleteMutation = useDeleteMutation();

  const { manageCommissionPeriods, isManageCommissionPeriodsPending } = useCommissionPeriods(
    form,
    teamMember.data?.data?.commission_periods ?? []
  );

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['TEAM_MEMBERS']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const { onUpdate, onPartialUpdate, onDelete, isDeleting, isPending, isSuccess } = useProfileCrudActions<
    TeamMemberFormSchema,
    TeamMemberUpdateSchema,
    { data: TeamMemberQuerySchema }
  >({
    form,
    edit,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Team Member has been updated.',
    deleteSuccessMessage: 'Team Member has been deleted.',
    formToMutation: teamMemberFormToUpdate,
    onSuccessSideEffects: handleSuccessSideEffects,
    onAfterUpdateSuccess: () => {
      if (form.getValues().lockdown_override_pin) {
        form.setValue('has_lockdown_override_pin', true);
      }
    },
    onDeleteSuccess: () => {
      nav({ to: '/team-members' });
    },
  });

  const onSubmit = useCallback(
    async (data: TeamMemberFormSchema) => {
      await manageCommissionPeriods();
      onUpdate(teamMemberFormToUpdate({ ...data }));
    },
    [onUpdate, manageCommissionPeriods]
  );

  const revisionHistory = useRevisionHistory(form, onUpdate);

  useEffect(() => {
    const { data: response } = teamMember;
    if (response) {
      resetFormState(form, teamMemberQueryToForm(response.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMember.data]);

  console.log(form.formState.errors);

  return {
    id,
    form,
    edit,
    isPending: isPending || isManageCommissionPeriodsPending,
    isSuccess,
    isDeleting,
    onSubmit,
    onDelete,
    onUpdate,
    onPartialUpdate,
    revisionHistory,
    isReverting: teamMember.isFetching || isPending,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
